import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { Helmet } from 'react-helmet';
import { partnerService } from '../../services/partnerService';
import Pagination from '../../components/ui/Pagination';
import { useToast } from '../../contexts/ToastContext';
import CountrySelect from '../../components/ui/CountrySelect';

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  active:      { label: 'Active',      color: 'bg-emerald-100 text-emerald-700 border-emerald-200',  dot: 'bg-emerald-500',  kanban: 'border-t-emerald-500' },
  prospect:    { label: 'Prospect',    color: 'bg-blue-100 text-blue-700 border-blue-200',            dot: 'bg-blue-500',     kanban: 'border-t-blue-500' },
  onboarding:  { label: 'Onboarding',  color: 'bg-amber-100 text-amber-700 border-amber-200',         dot: 'bg-amber-500',    kanban: 'border-t-amber-500' },
  suspended:   { label: 'Suspended',   color: 'bg-red-100 text-red-700 border-red-200',               dot: 'bg-red-500',      kanban: 'border-t-red-500' },
  inactive:    { label: 'Inactive',    color: 'bg-slate-100 text-slate-600 border-slate-200',         dot: 'bg-slate-400',    kanban: 'border-t-slate-400' },
  exited:      { label: 'Exited',      color: 'bg-gray-100 text-gray-600 border-gray-200',            dot: 'bg-gray-400',     kanban: 'border-t-gray-400' },
};

const TIER_CONFIG = {
  platinum: { label: 'Platinum', color: 'bg-violet-100 text-violet-700', icon: '💎' },
  gold:     { label: 'Gold',     color: 'bg-yellow-100 text-yellow-700', icon: '🥇' },
  silver:   { label: 'Silver',   color: 'bg-slate-100 text-slate-600',   icon: '🥈' },
  bronze:   { label: 'Bronze',   color: 'bg-orange-100 text-orange-700', icon: '🥉' },
};

const KANBAN_COLUMNS = ['prospect', 'onboarding', 'active', 'suspended', 'inactive', 'exited'];

const PARTNER_TYPES = ['Reseller', 'Distributor', 'System Integrator', 'ISV', 'MSP', 'Referral'];
const TIERS = ['Strategic', 'Gold', 'Silver', 'Tactical'];
const STATUSES = ['draft', 'submitted', 'under_review', 'approved', 'suspended', 'inactive'];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getInitials = (name) => {
  if (!name) return '?';
  return name?.split(' ')?.slice(0, 2)?.map(w => w?.[0])?.join('')?.toUpperCase();
};

const getAvatarGradient = (name) => {
  const gradients = [
    'from-blue-500 to-indigo-600',
    'from-violet-500 to-purple-600',
    'from-emerald-500 to-teal-600',
    'from-amber-500 to-orange-600',
    'from-rose-500 to-pink-600',
    'from-cyan-500 to-sky-600',
  ];
  const idx = (name?.charCodeAt(0) || 0) % gradients?.length;
  return gradients?.[idx];
};

// ─── Add Partner Modal ────────────────────────────────────────────────────────
const AddPartnerModal = ({ onClose, onSuccess }) => {
  const [form, setForm] = useState({
    legal_name: '',
    brand_name: '',
    tier: 'Silver',
    partner_type: 'Reseller',
    country: '',
    website: '',
    status: 'draft',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e?.target?.name]: e?.target?.value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.legal_name?.trim()) { setError('Company name is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      await partnerService?.create(form);
      onSuccess();
    } catch (err) {
      setError(err?.message || 'Failed to create partner.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      {/* Modal container: full screen on mobile, centered card on sm+ */}
      <div className="relative bg-white w-full h-full sm:h-auto sm:rounded-2xl sm:shadow-2xl sm:max-w-lg sm:max-h-[90vh] flex flex-col overflow-hidden">
        {/* Sticky Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-primary/5 to-secondary/5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
              <Icon name="UserPlus" size={18} className="text-white" />
            </div>
            <h2 className="text-base font-bold text-slate-900">Add New Partner</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Scrollable Form Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {error && (
            <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              <Icon name="AlertCircle" size={16} />
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Company Legal Name — full width */}
            <div className="col-span-1 sm:col-span-2">
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Company (Legal) Name *</label>
              <input
                name="legal_name"
                value={form?.legal_name}
                onChange={handleChange}
                placeholder="e.g. Acme Corporation Ltd."
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40"
                required
              />
            </div>

            {/* Brand Name */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Brand Name</label>
              <input
                name="brand_name"
                value={form?.brand_name}
                onChange={handleChange}
                placeholder="e.g. Acme"
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40"
              />
            </div>

            {/* Country */}
            <div>
              <CountrySelect
                label="Country"
                placeholder="Select country"
                value={form?.country}
                onChange={(val) => setForm(prev => ({ ...prev, country: val }))}
              />
            </div>

            {/* Partner Type */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Partner Type</label>
              <select
                name="partner_type"
                value={form?.partner_type}
                onChange={handleChange}
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 bg-white"
              >
                {PARTNER_TYPES?.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            {/* Tier */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Tier</label>
              <select
                name="tier"
                value={form?.tier}
                onChange={handleChange}
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 bg-white"
              >
                {TIERS?.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            {/* Initial Status — full width on mobile, half on sm */}
            <div className="col-span-1 sm:col-span-1">
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Initial Status</label>
              <select
                name="status"
                value={form?.status}
                onChange={handleChange}
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 bg-white"
              >
                {STATUSES?.map(s => <option key={s} value={s}>{s?.replace(/_/g, ' ')}</option>)}
              </select>
            </div>

            {/* Website — full width */}
            <div className="col-span-1 sm:col-span-2">
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Website</label>
              <input
                name="website"
                value={form?.website}
                onChange={handleChange}
                placeholder="https://example.com"
                className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40"
              />
            </div>
          </div>
        </div>

        {/* Sticky Footer */}
        <div className="flex-shrink-0 flex items-center gap-3 px-6 py-4 border-t border-slate-100 bg-white">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 min-h-[44px] px-5 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className="flex-1 min-h-[44px] px-5 py-2.5 rounded-xl bg-gradient-to-r from-primary to-secondary text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {saving ? (
              <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Saving...</>
            ) : (
              <><Icon name="UserPlus" size={16} /> Add Partner</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG?.[status] || STATUS_CONFIG?.inactive;
  return (
    <span className={`inline-flex items-center gap-4 px-8 py-4 rounded-full text-xs font-semibold border ${cfg?.color}`}>
      <span className={`w-5 h-5 rounded-full ${cfg?.dot}`}></span>
      {cfg?.label}
    </span>
  );
};

const TierBadge = ({ tier }) => {
  const cfg = TIER_CONFIG?.[tier?.toLowerCase()];
  if (!cfg) return null;
  return (
    <span className={`inline-flex items-center gap-4 px-8 py-4 rounded-full text-xs font-semibold ${cfg?.color}`}>
      {cfg?.icon} {cfg?.label}
    </span>
  );
};

const PartnerAvatar = ({ name, size = 'md' }) => {
  const sizes = { sm: 'w-8 h-8 text-xs', md: 'w-10 h-10 text-sm', lg: 'w-14 h-14 text-base' };
  return (
    <div className={`${sizes?.[size]} rounded-xl bg-gradient-to-br ${getAvatarGradient(name)} flex items-center justify-center text-white font-bold flex-shrink-0 shadow-sm`}>
      {getInitials(name)}
    </div>
  );
};

// ─── Edit Status Modal ────────────────────────────────────────────────────────
const EditStatusModal = ({ partner, onClose, onSuccess }) => {
  const [status, setStatus] = useState(partner?.status || 'active');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await partnerService?.updateStatus(partner?.id, status);
      onSuccess(partner?.id, status);
    } catch (err) {
      setError(err?.message || 'Failed to update status.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-primary/5 to-secondary/5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
              <Icon name="RefreshCw" size={16} className="text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900">Edit Status</h2>
              <p className="text-xs text-slate-500 truncate max-w-[180px]">{partner?.brand_name || partner?.legal_name}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>
        <div className="p-6 space-y-16">
          {error && (
            <div className="p-12 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-8">
              <Icon name="AlertCircle" size={14} /> {error}
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-8 uppercase tracking-wide">Partner Status</label>
            <div className="grid grid-cols-2 gap-8">
              {Object.entries(STATUS_CONFIG)?.map(([key, cfg]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setStatus(key)}
                  className={`flex items-center gap-8 px-12 py-10 rounded-xl border-2 text-sm font-semibold transition-all ${
                    status === key
                      ? 'border-primary bg-primary/5 text-primary' :'border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <span className={`w-8 h-8 rounded-full flex-shrink-0 ${cfg?.dot}`}></span>
                  {cfg?.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-12 px-6 py-4 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 min-h-[40px] px-16 py-8 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || status === partner?.status}
            className="flex-1 min-h-[40px] px-16 py-8 rounded-xl bg-gradient-to-r from-primary to-secondary text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-60 flex items-center justify-center gap-8"
          >
            {saving ? (
              <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Saving...</>
            ) : (
              <><Icon name="Check" size={14} /> Save Status</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── List View ────────────────────────────────────────────────────────────────
const ListView = ({ partners, onNavigate, onEditStatus }) => (
  <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="bg-slate-50/80 border-b border-slate-200/60">
            <th className="text-left px-24 py-16 text-xs font-bold text-slate-500 uppercase tracking-wider">Partner</th>
            <th className="text-left px-16 py-16 text-xs font-bold text-slate-500 uppercase tracking-wider">Type</th>
            <th className="text-left px-16 py-16 text-xs font-bold text-slate-500 uppercase tracking-wider">Tier</th>
            <th className="text-left px-16 py-16 text-xs font-bold text-slate-500 uppercase tracking-wider">Status</th>
            <th className="text-left px-16 py-16 text-xs font-bold text-slate-500 uppercase tracking-wider">Country</th>
            <th className="text-left px-16 py-16 text-xs font-bold text-slate-500 uppercase tracking-wider">Joined</th>
            <th className="px-16 py-16"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {partners?.map((partner) => (
            <tr key={partner?.id} className="hover:bg-slate-50/60 transition-colors cursor-pointer group" onClick={() => onNavigate(partner?.id)}>
              <td className="px-24 py-16">
                <div className="flex items-center gap-12">
                  <PartnerAvatar name={partner?.brand_name || partner?.legal_name} size="sm" />
                  <div>
                    <div className="text-sm font-semibold text-slate-900 group-hover:text-primary transition-colors">{partner?.brand_name || partner?.legal_name}</div>
                    <div className="text-xs text-slate-500 mt-2">{partner?.legal_name}</div>
                  </div>
                </div>
              </td>
              <td className="px-16 py-16"><span className="text-sm text-slate-600 capitalize">{partner?.partner_type?.replace(/_/g, ' ') || '—'}</span></td>
              <td className="px-16 py-16"><TierBadge tier={partner?.tier} /></td>
              <td className="px-16 py-16">
                <div className="flex items-center gap-8">
                  <StatusBadge status={partner?.status} />
                  <button
                    onClick={(e) => { e?.stopPropagation(); onEditStatus(partner); }}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-5 rounded-lg hover:bg-amber-50 text-amber-500 border border-transparent hover:border-amber-200"
                    title="Edit status"
                  >
                    <Icon name="Pencil" size={13} />
                  </button>
                </div>
              </td>
              <td className="px-16 py-16"><span className="text-sm text-slate-600">{partner?.country || '—'}</span></td>
              <td className="px-16 py-16"><span className="text-sm text-slate-500">{partner?.created_at ? new Date(partner.created_at)?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—'}</span></td>
              <td className="px-16 py-16">
                <button onClick={(e) => { e?.stopPropagation(); onNavigate(partner?.id); }} className="opacity-0 group-hover:opacity-100 transition-opacity p-6 rounded-lg hover:bg-primary/10 text-primary">
                  <Icon name="ArrowRight" size={16} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {partners?.length === 0 && (
        <div className="py-64 text-center text-slate-400">
          <Icon name="Users" size={40} className="mx-auto mb-12 opacity-30" />
          <p className="text-sm">No partners found</p>
        </div>
      )}
    </div>
  </div>
);

// ─── Cards View ───────────────────────────────────────────────────────────────
const CardsView = ({ partners, onNavigate, onEditStatus }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-20">
    {partners?.map((partner) => (
      <div key={partner?.id} onClick={() => onNavigate(partner?.id)} className="bg-white rounded-2xl border border-slate-200/60 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer group overflow-hidden">
        <div className={`h-1.5 w-full bg-gradient-to-r ${getAvatarGradient(partner?.brand_name || partner?.legal_name)}`}></div>
        <div className="p-20">
          <div className="flex items-start justify-between mb-16">
            <PartnerAvatar name={partner?.brand_name || partner?.legal_name} size="lg" />
            <div className="flex items-center gap-6">
              <StatusBadge status={partner?.status} />
              <button
                onClick={(e) => { e?.stopPropagation(); onEditStatus(partner); }}
                className="p-6 rounded-lg hover:bg-amber-50 text-amber-500 border border-transparent hover:border-amber-200 transition-colors opacity-0 group-hover:opacity-100"
                title="Edit status"
              >
                <Icon name="Pencil" size={13} />
              </button>
            </div>
          </div>
          <h3 className="text-sm font-bold text-slate-900 group-hover:text-primary transition-colors leading-tight mb-4">{partner?.brand_name || partner?.legal_name}</h3>
          <p className="text-xs text-slate-500 mb-12 truncate">{partner?.legal_name}</p>
          <div className="flex items-center gap-8 flex-wrap mb-16">
            <TierBadge tier={partner?.tier} />
            {partner?.partner_type && (
              <span className="text-xs text-slate-500 bg-slate-100 px-8 py-4 rounded-full capitalize">{partner?.partner_type?.replace(/_/g, ' ')}</span>
            )}
          </div>
          <div className="space-y-8 pt-12 border-t border-slate-100">
            {partner?.country && (
              <div className="flex items-center gap-8 text-xs text-slate-500">
                <Icon name="Globe" size={12} className="text-slate-400" />
                {partner?.country}
              </div>
            )}
            {partner?.created_at && (
              <div className="flex items-center gap-8 text-xs text-slate-500">
                <Icon name="Calendar" size={12} className="text-slate-400" />
                Joined {new Date(partner.created_at)?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
              </div>
            )}
          </div>
        </div>
        <div className="px-20 pb-16">
          <button className="w-full flex items-center justify-center gap-8 py-8 rounded-xl bg-slate-50 hover:bg-primary/10 hover:text-primary text-slate-600 text-xs font-semibold transition-colors group-hover:bg-primary/5">
            View Profile <Icon name="ArrowRight" size={12} />
          </button>
        </div>
      </div>
    ))}
    {partners?.length === 0 && (
      <div className="col-span-full py-64 text-center text-slate-400">
        <Icon name="Users" size={40} className="mx-auto mb-12 opacity-30" />
        <p className="text-sm">No partners found</p>
      </div>
    )}
  </div>
);

// ─── Kanban View ──────────────────────────────────────────────────────────────
const KanbanView = ({ partners, onNavigate, onEditStatus }) => {
  const columns = KANBAN_COLUMNS?.map(status => ({
    status,
    cfg: STATUS_CONFIG?.[status] || STATUS_CONFIG?.inactive,
    partners: partners?.filter(p => p?.status === status) || [],
  }));

  return (
    <div className="flex gap-16 overflow-x-auto pb-16 min-h-[500px]">
      {columns?.map(({ status, cfg, partners: colPartners }) => (
        <div key={status} className="flex-shrink-0 w-72">
          <div className="flex items-center justify-between mb-12 px-4">
            <div className="flex items-center gap-8">
              <span className={`w-8 h-8 rounded-full ${cfg?.dot}`}></span>
              <span className="text-sm font-bold text-slate-700">{cfg?.label}</span>
            </div>
            <span className="text-xs font-bold text-slate-400 bg-slate-100 px-8 py-4 rounded-full">{colPartners?.length}</span>
          </div>
          <div className="space-y-12">
            {colPartners?.map(partner => (
              <div key={partner?.id} onClick={() => onNavigate(partner?.id)} className={`bg-white rounded-xl border border-slate-200/60 border-t-4 ${cfg?.kanban} shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer group p-16`}>
                <div className="flex items-center gap-10 mb-12">
                  <PartnerAvatar name={partner?.brand_name || partner?.legal_name} size="sm" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-900 group-hover:text-primary transition-colors truncate">{partner?.brand_name || partner?.legal_name}</div>
                    <div className="text-xs text-slate-400 truncate">{partner?.partner_type?.replace(/_/g, ' ') || '—'}</div>
                  </div>
                  <button
                    onClick={(e) => { e?.stopPropagation(); onEditStatus(partner); }}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-5 rounded-lg hover:bg-amber-50 text-amber-500 flex-shrink-0"
                    title="Edit status"
                  >
                    <Icon name="Pencil" size={13} />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <TierBadge tier={partner?.tier} />
                  {partner?.country && (
                    <span className="text-xs text-slate-400 flex items-center gap-4">
                      <Icon name="MapPin" size={10} />
                      {partner?.country}
                    </span>
                  )}
                </div>
              </div>
            ))}
            {colPartners?.length === 0 && (
              <div className="border-2 border-dashed border-slate-200 rounded-xl py-32 text-center">
                <p className="text-xs text-slate-400">No partners</p>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const AllPartners = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('cards');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterTier, setFilterTier] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editStatusPartner, setEditStatusPartner] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  // Pre-apply sort from location state (e.g. from Executive Dashboard)
  const [sortBy, setSortBy] = useState(() => location?.state?.sortBy || null);
  const PAGE_SIZE = 12;

  useEffect(() => {
    loadPartners();
  }, []);

  // Reload when wizard creates a new partner
  useEffect(() => {
    const handleWizardComplete = () => {
      loadPartners();
    };
    window.addEventListener('wizard:partner-created', handleWizardComplete);
    return () => window.removeEventListener('wizard:partner-created', handleWizardComplete);
  }, []);

  const loadPartners = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await partnerService?.getAll();
      setPartners(data);
    } catch (err) {
      console.error('Error loading partners:', err);
      setError(err?.message);
      toast?.error('Failed to load partners. Please retry.');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    setCurrentPage(1);
    let result = partners?.filter(p => {
      const name = (p?.brand_name || p?.legal_name || '')?.toLowerCase();
      const matchSearch = !search || name?.includes(search?.toLowerCase());
      const matchStatus = filterStatus === 'all' || p?.status === filterStatus;
      const matchTier = filterTier === 'all' || p?.tier?.toLowerCase() === filterTier?.toLowerCase();
      return matchSearch && matchStatus && matchTier;
    });
    // Sort by health score descending if requested
    if (sortBy === 'health_score') {
      result = [...result]?.sort((a, b) => (b?.health_score || 0) - (a?.health_score || 0));
    }
    return result;
  }, [partners, search, filterStatus, filterTier, sortBy]);

  const totalPages = Math.ceil((filtered?.length || 0) / PAGE_SIZE);
  const paginated = filtered?.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const stats = useMemo(() => ({
    total: partners?.length || 0,
    active: partners?.filter(p => p?.status === 'active' || p?.status === 'approved')?.length || 0,
    onboarding: partners?.filter(p => p?.status === 'onboarding' || p?.status === 'under_review')?.length || 0,
    prospect: partners?.filter(p => p?.status === 'prospect' || p?.status === 'draft')?.length || 0,
  }), [partners]);

  const handleNavigate = (partnerId) => {
    navigate(partnerId ? `/partner-profile-portal/${partnerId}` : '/partner-profile-portal');
  };

  const handleStatusUpdated = (partnerId, newStatus) => {
    setPartners(prev => prev?.map(p => p?.id === partnerId ? { ...p, status: newStatus } : p));
    setEditStatusPartner(null);
    toast?.success('Partner status updated successfully!');
  };

  const viewButtons = [
    { mode: 'cards',  icon: 'LayoutGrid',  label: 'Cards' },
    { mode: 'list',   icon: 'List',        label: 'List' },
    { mode: 'kanban', icon: 'Columns',     label: 'Kanban' },
  ];

  return (
    <>
      <Helmet>
        <title>All Partners — PartnerHub</title>
      </Helmet>
      <div className="min-h-screen bg-slate-50/50">
        <Header />
        <main className="pt-[148px] pb-48 px-24 max-w-[1600px] mx-auto">

          {/* Page Header */}
          <div className="mb-32">
            <div className="flex items-start justify-between flex-wrap gap-16">
              <div>
                <div className="flex items-center gap-12 mb-8">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-primary to-secondary rounded-xl blur-md opacity-40"></div>
                    <div className="relative flex items-center justify-center w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-xl shadow-lg">
                      <Icon name="Users" size={22} className="text-white" />
                    </div>
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-slate-900 font-heading">All Partners</h1>
                    <p className="text-sm text-slate-500 mt-2">Complete partner directory across all lifecycle stages</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-12">
                {/* Add Partner Button */}
                <button
                  onClick={() => setShowAddModal(true)}
                  className="flex items-center gap-8 px-20 py-10 bg-gradient-to-r from-primary to-secondary text-white rounded-xl text-sm font-semibold shadow-md hover:shadow-lg transition-all"
                >
                  <Icon name="Plus" size={16} />
                  New Partner
                </button>

                {/* View Switcher */}
                <div className="flex items-center gap-4 bg-white rounded-xl border border-slate-200/60 p-4 shadow-sm">
                  {viewButtons?.map(({ mode, icon, label }) => (
                    <button
                      key={mode}
                      onClick={() => setViewMode(mode)}
                      className={`flex items-center gap-8 px-16 py-8 rounded-lg text-sm font-semibold transition-all ${
                        viewMode === mode
                          ? 'bg-gradient-to-r from-primary to-secondary text-white shadow-sm'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                      }`}
                    >
                      <Icon name={icon} size={16} />
                      <span className="hidden sm:inline">{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Stats Bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-12 mt-24">
              {[
                { label: 'Total Partners', value: stats?.total, icon: 'Users', color: 'from-primary to-secondary' },
                { label: 'Active',         value: stats?.active, icon: 'CheckCircle', color: 'from-emerald-500 to-teal-600' },
                { label: 'Onboarding',     value: stats?.onboarding, icon: 'Clock', color: 'from-amber-500 to-orange-600' },
                { label: 'Prospects',      value: stats?.prospect, icon: 'Target', color: 'from-blue-500 to-indigo-600' },
              ]?.map(({ label, value, icon, color }) => (
                <div key={label} className="bg-white rounded-xl border border-slate-200/60 shadow-sm p-16 flex items-center gap-12">
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center flex-shrink-0`}>
                    <Icon name={icon} size={18} className="text-white" />
                  </div>
                  <div>
                    <div className="text-xl font-bold text-slate-900 font-data">{value}</div>
                    <div className="text-xs text-slate-500">{label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-12 mb-24">
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Icon name="Search" size={16} className="absolute left-12 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search partners..."
                value={search}
                onChange={e => setSearch(e?.target?.value)}
                className="w-full pl-36 pr-12 py-10 text-sm bg-white border border-slate-200/60 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 shadow-sm"
              />
            </div>
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e?.target?.value)}
              className="px-12 py-10 text-sm bg-white border border-slate-200/60 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 shadow-sm text-slate-700"
            >
              <option value="all">All Statuses</option>
              {Object.entries(STATUS_CONFIG)?.map(([key, cfg]) => (
                <option key={key} value={key}>{cfg?.label}</option>
              ))}
            </select>
            <select
              value={filterTier}
              onChange={e => setFilterTier(e?.target?.value)}
              className="px-12 py-10 text-sm bg-white border border-slate-200/60 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 shadow-sm text-slate-700"
            >
              <option value="all">All Tiers</option>
              {Object.entries(TIER_CONFIG)?.map(([key, cfg]) => (
                <option key={key} value={key}>{cfg?.icon} {cfg?.label}</option>
              ))}
            </select>
            <div className="ml-auto text-sm text-slate-500">{filtered?.length} of {partners?.length} partners</div>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-24 p-16 bg-red-50 border border-red-200 rounded-xl flex items-center gap-12 text-red-700">
              <Icon name="AlertCircle" size={18} />
              <span className="text-sm">{error}</span>
              <button onClick={loadPartners} className="ml-auto text-xs font-semibold underline">Retry</button>
            </div>
          )}

          {/* Loading */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-96 gap-16">
              <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
              <p className="text-sm text-slate-500">Loading partners...</p>
            </div>
          ) : (
            <>
              {viewMode === 'list'   && <ListView   partners={paginated} onNavigate={handleNavigate} onEditStatus={setEditStatusPartner} />}
              {viewMode === 'cards'  && <CardsView  partners={paginated} onNavigate={handleNavigate} onEditStatus={setEditStatusPartner} />}
              {viewMode === 'kanban' && <KanbanView partners={filtered}  onNavigate={handleNavigate} onEditStatus={setEditStatusPartner} />}

              {/* Pagination (not shown for kanban) */}
              {viewMode !== 'kanban' && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={filtered?.length}
                  pageSize={PAGE_SIZE}
                  onPageChange={setCurrentPage}
                />
              )}
            </>
          )}
        </main>
      </div>

      {showAddModal && (
        <AddPartnerModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            loadPartners();
            toast?.success('Partner created successfully!');
          }}
        />
      )}

      {editStatusPartner && (
        <EditStatusModal
          partner={editStatusPartner}
          onClose={() => setEditStatusPartner(null)}
          onSuccess={handleStatusUpdated}
        />
      )}
    </>
  );
};

export default AllPartners;
